import './text-editor.scss';

import { LinkNode } from '@lexical/link';
import { ListItemNode, ListNode } from '@lexical/list';
import { AutoFocusPlugin } from '@lexical/react/LexicalAutoFocusPlugin';
import { CheckListPlugin } from '@lexical/react/LexicalCheckListPlugin';
import { LexicalComposer } from '@lexical/react/LexicalComposer';
import { ContentEditable } from '@lexical/react/LexicalContentEditable';
import { LexicalErrorBoundary } from '@lexical/react/LexicalErrorBoundary';
import { HistoryPlugin } from '@lexical/react/LexicalHistoryPlugin';
import { LinkPlugin } from '@lexical/react/LexicalLinkPlugin';
import { ListPlugin } from '@lexical/react/LexicalListPlugin';
import { RichTextPlugin } from '@lexical/react/LexicalRichTextPlugin';
import { ParagraphNode, TextNode } from 'lexical';

import { CustomFormattedMessage } from '../intl/custom-formatted-message';
import { BlockType } from './elements/block-options-dropdown-list/block-options-dropdown-list';
import { CustomLinkNode } from './nodes/CustomLinkNode';
import { OnChangePlugin } from './plugins/on-change-plugin/on-change-plugin';
import { ToolbarPlugin } from './plugins/toolbar-plugin/toolbar-plugin';
import { editorTheme } from './text-editor-theme';
// https://codesandbox.io/p/sandbox/vigilant-kate-5tncvy?file=%2Fsrc%2Fstyles.css%3A681%2C7-681%2C16
// https://github.com/facebook/lexical/blob/df72ec4fed6747b3215dbfe9a1e0478fc9383673/packages/lexical-playground/src/plugins/ToolbarPlugin/index.tsx

const placeholder = 'Enter some rich text...';

type TextEditorProps = {
  html?: string;
  blockTypes?: BlockType[];
  onChange?: (html: string) => void;
};

const initialConfig = {
  namespace: 'Doccs Editor',
  theme: editorTheme,
  onError(error: Error) {
    throw error;
  },
  nodes: [
    ParagraphNode,
    TextNode,
    ListNode,
    ListItemNode,

    CustomLinkNode,
    {
      replace: LinkNode,
      with: (node: LinkNode) =>
        new CustomLinkNode(node.getURL(), {
          target: node.getTarget(),
          rel: node.getRel(),
          title: node.getTitle(),
        }),
    },
  ],
  editorState: null,
};

export function TextEditor({ html, onChange, blockTypes }: TextEditorProps) {
  return (
    <LexicalComposer initialConfig={initialConfig}>
      <div className="text-editor">
        <ToolbarPlugin blockTypes={blockTypes} />
        <div className="editor-inner">
          <RichTextPlugin
            contentEditable={<ContentEditable className="editor-input" />}
            aria-placeholder={placeholder}
            placeholder={
              <div className="editor-placeholder">
                <CustomFormattedMessage id="editor.placeholder" />
              </div>
            }
            ErrorBoundary={LexicalErrorBoundary}
          />

          <HistoryPlugin />
          <AutoFocusPlugin />
          <LinkPlugin />
          <ListPlugin />
          <CheckListPlugin />
          <OnChangePlugin value={html} onChange={onChange} />
        </div>
      </div>
    </LexicalComposer>
  );
}
